// Bibliothek: PGN importieren, suchen, Partien öffnen, fürs Wallpaper auswählen.
import { h, type VNode } from 'snabbdom';
import { api, type GameRow, type ImportReport } from '../api';
import { debounce, fullMoves } from '../util';
import { t } from '../i18n';
import { isPublic } from '../util';

export class LibraryCtrl {
  query = '';
  games: GameRow[] = [];
  reviewed = new Set<number>();
  total = 0;
  loading = false;
  error = '';
  report: ImportReport | null = null;
  importing = false;
  dragOver = false;
  pasteOpen = false;
  pasteText = '';
  confirmDelete: number | null = null;

  constructor(readonly redraw: () => void) {}

  load = async (append = false) => {
    this.loading = true;
    this.redraw();
    try {
      const page = await api.games(this.query, append ? this.games.length : 0);
      this.games = append ? this.games.concat(page.games) : page.games;
      this.total = page.total;
      this.reviewed = new Set(page.reviewed);
      this.error = '';
    } catch (e) {
      this.error = t('Bibliothek nicht geladen: {e}', { e: (e as Error).message });
    }
    this.loading = false;
    this.redraw();
  };

  search = debounce((q: string) => {
    this.query = q;
    this.load();
  }, 200);

  async importFiles(files: File[]) {
    if (!files.length) return;
    await this.runImport(() => api.importFiles(files));
  }

  async importPaste() {
    if (!this.pasteText.trim()) return;
    await this.runImport(() => api.importText(this.pasteText));
    if (!this.error) {
      this.pasteText = '';
      this.pasteOpen = false;
    }
  }

  private async runImport(f: () => Promise<ImportReport>) {
    this.importing = true;
    this.report = null;
    this.redraw();
    try {
      this.report = await f();
      this.error = '';
      await this.load();
    } catch (e) {
      this.error = t('Import fehlgeschlagen: {e}', { e: (e as Error).message });
    }
    this.importing = false;
    this.redraw();
  }

  async toggleWallpaper(g: GameRow) {
    try {
      await api.setWallpaper(g.id, !g.inWallpaper);
      g.inWallpaper = !g.inWallpaper;
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.redraw();
  }

  async remove(g: GameRow) {
    if (this.confirmDelete !== g.id) {
      this.confirmDelete = g.id;
      this.redraw();
      return;
    }
    this.confirmDelete = null;
    try {
      await api.remove(g.id);
      await this.load();
    } catch (e) {
      this.error = (e as Error).message;
      this.redraw();
    }
  }
}

function reportView(r: ImportReport): VNode {
  const parts = [t('{a} von {b} Partien importiert', { a: r.imported, b: r.games })];
  if (r.duplicates) parts.push(t('{n} waren schon da', { n: r.duplicates }));
  return h('div.report', [
    h('p', parts.join(', ') + '.'),
    r.problems.length
      ? h('details', [
          h('summary', r.problems.length === 1 ? t('1 Partie mit Fehlern (bis zum Fehler übernommen oder übersprungen)') : t('{n} Partien mit Fehlern (bis zum Fehler übernommen oder übersprungen)', { n: r.problems.length })),
          h(
            'ul',
            r.problems.slice(0, 50).map(p =>
              h('li', `${t('Partie {i}', { i: p.index })}${p.white || p.black ? ` (${p.white ?? '?'} – ${p.black ?? '?'})` : ''}: ${p.error}`),
            ),
          ),
        ])
      : null,
  ]);
}

/** Klick irgendwo in die Zeile öffnet die Partie, außer auf Häkchen, Knöpfe und Links */
function openRow(e: MouseEvent, id: number) {
  if (e.target instanceof Element && e.target.closest('a, button, input, label')) return;
  location.hash = `#/partie/${id}`;
}

export function libraryView(c: LibraryCtrl): VNode {
  const dropHandlers = {
    dragover: (e: DragEvent) => {
      e.preventDefault();
      if (!c.dragOver) {
        c.dragOver = true;
        c.redraw();
      }
    },
    dragleave: () => {
      c.dragOver = false;
      c.redraw();
    },
    drop: (e: DragEvent) => {
      e.preventDefault();
      c.dragOver = false;
      c.importFiles(Array.from(e.dataTransfer?.files ?? []));
    },
  };
  return h('section.library', [
    isPublic ? null : h('label.drop', { class: { over: c.dragOver, busy: c.importing }, on: dropHandlers }, [
      h('span.btn.accent', c.importing ? t('Importiere …') : t('PGN importieren')),
      h('span.muted', t('Dateien hierher ziehen oder klicken. Sammeldateien werden in einzelne Partien zerlegt.')),
      h('input', {
        attrs: { type: 'file', accept: '.pgn,text/plain', multiple: true, hidden: true },
        on: {
          change: (e: Event) => {
            const input = e.target as HTMLInputElement;
            c.importFiles(Array.from(input.files ?? []));
            input.value = '';
          },
        },
      }),
    ]),
    h('div.row', [
      isPublic ? null : h('button.btn', { on: { click: () => ((c.pasteOpen = !c.pasteOpen), c.redraw()) } }, c.pasteOpen ? t('Einfügen schließen') : t('PGN einfügen')),
      h('input.search', {
        attrs: { type: 'search', placeholder: t('Suchen: Spieler, Ereignis, Ort, Jahr'), 'aria-label': t('Bibliothek durchsuchen'), value: c.query },
        on: { input: (e: Event) => c.search((e.target as HTMLInputElement).value) },
      }),
      h('span.muted.count', c.loading ? t('lade …') : t('{a} von {b}', { a: c.games.length, b: c.total })),
    ]),
    c.pasteOpen
      ? h('div.paste', [
          h('textarea', {
            attrs: { rows: 8, placeholder: '[Event "…"]\n\n1. e4 e5 …', 'aria-label': t('PGN-Text') },
            props: { value: c.pasteText },
            on: { input: (e: Event) => (c.pasteText = (e.target as HTMLTextAreaElement).value) },
          }),
          h('button.btn.accent', { on: { click: () => c.importPaste() } }, t('Importieren')),
        ])
      : null,
    c.error ? h('p.error', c.error) : null,
    c.report ? reportView(c.report) : null,
    c.games.length
      ? h('div.table-wrap', [
          h('table.games', [
            h('thead', h('tr', (isPublic ? ['Weiß', 'Schwarz', 'Jahr', 'Ergebnis', 'Ereignis', 'Züge', 'Analyse'] : ['Weiß', 'Schwarz', 'Jahr', 'Ergebnis', 'Ereignis', 'Züge', 'Analyse', 'Wallpaper', '']).map(s => h('th', s ? t(s) : '')))),
            h(
              'tbody',
              c.games.map(g =>
                h('tr', { key: g.id, on: { click: (e: MouseEvent) => openRow(e, g.id) } }, [
                  h('td', h('a', { attrs: { href: `#/partie/${g.id}` } }, g.white ?? '?')),
                  h('td', h('a', { attrs: { href: `#/partie/${g.id}` } }, g.black ?? '?')),
                  h('td.num', g.year ?? h('span.muted', '—')),
                  h('td.res', g.result ?? ''),
                  h('td', g.event ?? g.site ?? h('span.muted', '—')),
                  h('td.num', [String(fullMoves(g.plies)), g.error ? h('span.warn', { attrs: { title: g.error } }, ' ' + t('gekürzt')) : null]),
                  h('td', c.reviewed.has(g.id) ? h('span.tag', t('ausgewertet')) : ''),
                  isPublic ? null : h('td', h('input', {
                    attrs: { type: 'checkbox', 'aria-label': t('Im Wallpaper abspielen') },
                    props: { checked: g.inWallpaper },
                    on: { change: () => c.toggleWallpaper(g) },
                  })),
                  isPublic ? null : h('td', h('button.link', { class: { danger: c.confirmDelete === g.id }, on: { click: () => c.remove(g) } },
                    c.confirmDelete === g.id ? t('Wirklich löschen?') : t('Löschen'))),
                ]),
              ),
            ),
          ]),
          c.games.length < c.total ? h('button.btn.more', { on: { click: () => c.load(true) } }, t('Weitere laden')) : null,
        ])
      : !c.loading && !c.error
        ? h('p.empty', c.query ? t('Keine Partie passt zur Suche.') : isPublic ? t('Noch keine Partien.') : t('Noch keine Partien. Importiere eine PGN-Datei.'))
        : null,
  ]);
}
